import React, { createContext, useContext, useMemo } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import { createEntitlementLoader } from '@/services/entitlement-loader'
import { EntitlementKey } from '@/services/entitlement-loader/types'
import DataLoader from '@/vendor/dataloader'

// When the provider is mounted but the loader isn't ready yet (e.g., no access token),
// we set context to null. If the provider is missing entirely, context will be undefined.
type LoaderContextType = DataLoader<EntitlementKey, boolean> | null | undefined

const LoaderContext = createContext<LoaderContextType>(undefined)

type EntitlementLoaderProviderProps = {
  children: React.ReactNode
}

export const EntitlementLoaderProvider: React.FC<
  EntitlementLoaderProviderProps
> = ({ children }) => {
  const { accessToken } = useAuthInfo()
  const loader = useMemo(() => {
    return accessToken ? createEntitlementLoader(accessToken) : null
  }, [accessToken])

  return (
    <LoaderContext.Provider value={loader}>{children}</LoaderContext.Provider>
  )
}

export const useEntitlementLoader = () => {
  const context = useContext(LoaderContext)

  if (context === undefined) {
    throw new Error(
      'useEntitlementLoader must be used within an EntitlementLoaderProvider',
    )
  }

  return context
}
